
import { isApiSuccess } from "@/components/xcoins/lottery/lotteryUtils";
import {
  buildLotteryClaimPayload,
  buildStoreAddressPayload,
  getAddressId,
  getAddressMobile,
  getAllAddress,
  getClaimRecordId,
  getCountryAreaId,
  getCountryAreaIso,
  getCountryAreaName,
  getProvinceAreaId,
  getProvinceAreaName,
  getReceiverName,
  isDefaultAddress,
  isStoreAreaApiSuccess,
  normalizeAddressList,
  validateAddressDetail,
  validateEmail,
  validatePhone,
  validateReceiverName,
  validateRegion,
  validateZipcode,
} from "@/utils/mallAddress";
import { getDialogSizeByViewport } from "@/utils/dialogResponsive";

export default {
  name: "LotteryAddressDialog",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    drawResult: {
      type: Object,
      default: () => ({}),
    },
    mode: {
      type: String,
      default: "claim",
    },
    selectedId: {
      type: [String, Number],
      default: "",
    },
  },
  data() {
    return {
      addressFormVisible: false,
      addressBookLoading: false,
      addressSaving: false,
      addressConfirming: false,
      addressDeleting: false,
      addressList: [],
      selectedAddressId: "",
      countryLoading: false,
      provinceLoading: false,
      countryList: [],
      provinceList: [],
      addressForm: this.getEmptyAddressForm(),
      isMobile: false,
      bookDialogWidth: "520px",
      formDialogWidth: "610px",
    };
  },
  computed: {
    sortedAddressList() {
      return [...this.addressList].sort((a, b) => {
        return Number(this.isDefaultAddress(b)) - Number(this.isDefaultAddress(a));
      });
    },
    selectedCountryIso() {
      const country = this.countryList.find((item) => {
        return String(this.getCountryAreaId(item)) === String(this.addressForm.country);
      });
      return this.getCountryAreaIso(country);
    },
    addressRules() {
      return {
        firstName: [{ validator: this.validateNameField, trigger: "blur" }],
        lastName: [{ validator: this.validateNameField, trigger: "blur" }],
        address1: [{ validator: this.validateAddress1Field, trigger: "blur" }],
        country: [{ validator: this.validateCountryField, trigger: "change" }],
        province: [{ validator: this.validateProvinceField, trigger: "change" }],
        cityName: [{ validator: this.validateCityField, trigger: "blur" }],
        zipcode: [{ validator: this.validateZipField, trigger: "blur" }],
        mobile: [{ validator: this.validateMobileField, trigger: "blur" }],
        email: [{ validator: this.validateEmailField, trigger: "blur" }],
      };
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.updateViewport();
        this.fetchAddressList();
      } else {
        this.closeAddressForm();
      }
    },
  },
  mounted() {
    this.updateViewport();
    if (process.client) {
      window.addEventListener("resize", this.updateViewport);
    }
  },
  beforeDestroy() {
    if (process.client) {
      window.removeEventListener("resize", this.updateViewport);
    }
  },
  methods: {
    getAddressId,
    getAddressMobile,
    getAllAddress,
    getCountryAreaId,
    getCountryAreaIso,
    getCountryAreaName,
    getProvinceAreaId,
    getProvinceAreaName,
    getReceiverName,
    isDefaultAddress,
    /** 按 768 断点更新弹窗宽度 */
    updateViewport() {
      if (!process.client) {
        return;
      }
      const { isMobile } = getDialogSizeByViewport(document.body.clientWidth);
      this.isMobile = isMobile;
      this.bookDialogWidth = isMobile ? "90%" : "520px";
      this.formDialogWidth = isMobile ? "90%" : "610px";
    },
    getEmptyAddressForm() {
      return {
        addressId: "",
        firstName: "",
        lastName: "",
        address1: "",
        address2: "",
        country: "",
        province: "",
        cityName: "",
        district: "",
        zipcode: "",
        mobile: "",
        email: "",
        isDefault: false,
      };
    },
    /** 将工具函数错误 key 转成 i18n 文案 */
    messageForError(key) {
      if (key === "EMAIL_INVALID") {
        return this.$t("page.EMAIL_INVALID");
      }
      return this.$t(`xcoins.${key}`);
    },
    validateNameField(rule, value, callback) {
      const errorKey = validateReceiverName(value);
      errorKey ? callback(new Error(this.messageForError(errorKey))) : callback();
    },
    validateAddress1Field(rule, value, callback) {
      const errorKey = validateAddressDetail(value);
      errorKey ? callback(new Error(this.messageForError(errorKey))) : callback();
    },
    validateCountryField(rule, value, callback) {
      const errorKey = validateRegion(value);
      errorKey ? callback(new Error(this.messageForError(errorKey))) : callback();
    },
    validateProvinceField(rule, value, callback) {
      if (!this.provinceList.length) {
        callback();
        return;
      }
      const errorKey = validateRegion(value);
      errorKey ? callback(new Error(this.messageForError(errorKey))) : callback();
    },
    validateCityField(rule, value, callback) {
      const errorKey = validateRegion(value);
      errorKey ? callback(new Error(this.messageForError(errorKey))) : callback();
    },
    validateZipField(rule, value, callback) {
      const errorKey = validateZipcode(value);
      errorKey ? callback(new Error(this.messageForError(errorKey))) : callback();
    },
    validateMobileField(rule, value, callback) {
      const errorKey = validatePhone(value, this.selectedCountryIso);
      errorKey ? callback(new Error(this.messageForError(errorKey))) : callback();
    },
    validateEmailField(rule, value, callback) {
      const errorKey = validateEmail(value);
      errorKey ? callback(new Error(this.messageForError(errorKey))) : callback();
    },
    closeAddressBook() {
      this.$emit("close");
    },
    openAddressForm() {
      this.resetAddressForm();
      this.addressForm.email = this.getCurrentUserEmail();
      this.addressFormVisible = true;
      this.fetchCountryList();
    },
    closeAddressForm() {
      this.addressFormVisible = false;
      this.resetAddressForm();
    },
    resetAddressForm() {
      this.addressForm = this.getEmptyAddressForm();
      this.provinceList = [];
      if (this.$refs.addressFormRef) {
        this.$refs.addressFormRef.clearValidate();
      }
    },
    /** 官网国家列表，成功码为数字 200 */
    async fetchCountryList() {
      if (this.countryLoading || this.countryList.length) {
        return;
      }

      this.countryLoading = true;
      try {
        const res = await this.$global.pointsAddressCountryList();
        this.countryList = isStoreAreaApiSuccess(res) && Array.isArray(res.data)
          ? res.data
          : [];
      } catch (e) {
        this.countryList = [];
      } finally {
        this.countryLoading = false;
      }
    },
    /** 按国家拉取省/州，参数只认 countryId */
    async fetchProvinceList(countryId) {
      if (!countryId) {
        this.provinceList = [];
        return;
      }

      this.provinceLoading = true;
      try {
        const res = await this.$global.pointsAddressChildrenAreaList({
          countryId,
        });
        this.provinceList = isStoreAreaApiSuccess(res) && Array.isArray(res.data)
          ? res.data
          : [];
      } catch (e) {
        this.provinceList = [];
      } finally {
        this.provinceLoading = false;
      }
    },
    handleCountryChange(countryId) {
      this.addressForm.province = "";
      this.fetchProvinceList(countryId);
    },
    /** 地址簿只认 data 数组与 id */
    async fetchAddressList(preferredId = "") {
      if (this.addressBookLoading) {
        return;
      }

      this.addressBookLoading = true;
      try {
        const res = await this.$pointsCache.requestWithPointToken((token) => {
          return this.$global.pointsLotteryAddressList(undefined, token);
        });

        if (isApiSuccess(res)) {
          const list = normalizeAddressList(res);
          this.addressList = list;
          const targetId = preferredId || this.selectedId;
          const preferred = targetId
            ? list.find((item) => String(this.getAddressId(item)) === String(targetId))
            : null;
          const defaultAddress = preferred || list.find((item) => this.isDefaultAddress(item)) || list[0];
          this.selectedAddressId = defaultAddress ? this.getAddressId(defaultAddress) : "";
        }
      } catch (e) {
        this.addressList = [];
      } finally {
        this.addressBookLoading = false;
      }
    },
    selectAddress(item) {
      this.selectedAddressId = this.getAddressId(item);
    },
    handleAddressCommand(command) {
      if (!command || !command.item) {
        return;
      }
      if (command.type === "edit") {
        this.openEditAddressForm(command.item);
        return;
      }
      if (command.type === "delete") {
        this.deleteAddress(command.item);
      }
    },
    /** 编辑回填只认 StoreAddressRes 字段 */
    async openEditAddressForm(item) {
      this.addressForm = {
        addressId: this.getAddressId(item),
        firstName: item.firstName || "",
        lastName: item.lastName || "",
        address1: item.address1 || "",
        address2: item.address2 || "",
        country: item.country || "",
        province: item.province || "",
        cityName: item.cityName || "",
        district: item.district || "",
        zipcode: item.zipcode || "",
        mobile: this.getAddressMobile(item),
        email: item.email || this.getCurrentUserEmail(),
        isDefault: this.isDefaultAddress(item),
      };
      this.addressFormVisible = true;
      await this.fetchCountryList();
      if (this.addressForm.country) {
        await this.fetchProvinceList(this.addressForm.country);
      }
    },
    async deleteAddress(item) {
      const addressId = this.getAddressId(item);
      if (!addressId || this.addressDeleting) {
        return;
      }

      try {
        await this.$xcoinsConfirm({
          title: this.$t("xcoins.DELETE"),
          message: this.$t("xcoins.CONFIRM_DELETE"),
          confirmText: this.$t("xcoins.CONFIRM"),
          cancelText: this.$t("page.CANCEL"),
        });
      } catch (e) {
        return;
      }

      this.addressDeleting = true;
      try {
        const res = await this.$pointsCache.requestWithPointToken((token) => {
          return this.$global.pointsLotteryAddressDelete({ addressId }, token);
        });

        if (isApiSuccess(res)) {
          this.$message.success(this.$t("xcoins.ADDR_DELETED"));
          if (String(this.selectedAddressId) === String(addressId)) {
            this.selectedAddressId = "";
          }
          await this.fetchAddressList();
          return;
        }

        if (res) {
          this.$message.error(res.msg || this.$t("xcoins.DRAW_FAILED"));
        }
      } catch (e) {
        this.$message.error(this.$t("xcoins.DRAW_FAILED"));
      } finally {
        this.addressDeleting = false;
      }
    },
    async saveAddress() {
      if (this.addressSaving) {
        return;
      }

      const formRef = this.$refs.addressFormRef;
      const valid = await new Promise((resolve) => {
        if (!formRef) {
          resolve(false);
          return;
        }
        formRef.validate((ok) => resolve(ok));
      });
      if (!valid) {
        this.$message.warning(this.$t("xcoins.COMPLETE_ADDR_TIP"));
        return;
      }

      this.addressSaving = true;
      try {
        const payload = buildStoreAddressPayload(this.addressForm, this.getCurrentUserEmail());
        const res = await this.$pointsCache.requestWithPointToken((token) => {
          return payload.addressId
            ? this.$global.pointsLotteryAddressUpdate(payload, token)
            : this.$global.pointsLotteryAddressAdd(payload, token);
        });

        if (isApiSuccess(res)) {
          this.$message.success(this.$t("xcoins.ADDR_SAVED"));
          const savedId = this.addressForm.addressId || (res.data && res.data.id) || "";
          this.closeAddressForm();
          await this.fetchAddressList(savedId);
          if (this.mode === "select") {
            const selectedAddress = this.getSelectedAddress();
            if (selectedAddress) {
              this.$emit("confirm", selectedAddress);
              this.closeAddressBook();
            }
          }
          return;
        }

        if (res) {
          this.$message.error(res.msg || this.$t("xcoins.DRAW_FAILED"));
        }
      } catch (e) {
        this.$message.error(this.$t("xcoins.DRAW_FAILED"));
      } finally {
        this.addressSaving = false;
      }
    },
    getCurrentUserEmail() {
      return this.$store.state.userInfo?.userInfo?.email || this.$store.state.userInfo?.email || "";
    },
    getSelectedAddress() {
      return this.addressList.find((item) => {
        return String(this.getAddressId(item)) === String(this.selectedAddressId);
      });
    },
    async confirmSelectedAddress() {
      if (!this.selectedAddressId || this.addressConfirming) {
        return;
      }

      const selectedAddress = this.getSelectedAddress();
      if (!selectedAddress) {
        return;
      }

      if (this.mode === "select") {
        this.$emit("confirm", selectedAddress);
        this.closeAddressBook();
        return;
      }

      this.addressConfirming = true;
      try {
        const res = await this.$pointsCache.requestWithPointToken((token) => {
          return this.$global.pointsLotteryPrizeClaim(
            buildLotteryClaimPayload(
              selectedAddress,
              getClaimRecordId(this.drawResult),
              this.getCurrentUserEmail()
            ),
            token
          );
        });

        if (isApiSuccess(res)) {
          this.$message.success(this.$t("xcoins.ADDR_SUBMITTED"));
          this.closeAddressBook();
          this.$emit("claimed");
          return;
        }

        if (res) {
          this.$message.error(res.msg || this.$t("xcoins.DRAW_FAILED"));
        }
      } catch (e) {
        this.$message.error(this.$t("xcoins.DRAW_FAILED"));
      } finally {
        this.addressConfirming = false;
      }
    },
  },
};
